import type { LiveSummaryItemData, LiveSummaryTag } from "./types";

/**
 * The two Smart Tags a project starts with, as a surface that cannot read the
 * project's vocabulary sees them — the public shared-session page, when its API
 * sends none. Names and colours mirror what the control plane seeds,
 * and a project that renamed or recoloured one hands down its own row instead,
 * which wins.
 *
 * Ids are namespaced rather than random so nothing can collide with a real
 * label id, and so filtering on one is stable across a reload.
 */
export const BUILT_IN_DEFAULT_TAGS: readonly LiveSummaryTag[] = [
  { id: "default:todo", name: "To-dos", color: "#f59e0b", smart: true, defaultKind: "todo" },
  { id: "default:decision", name: "Decisions", color: "#8b5cf6", smart: true, defaultKind: "decision" },
];

/**
 * Every tag an item carries, from the three places they come from: what a person
 * put on it, what a Smart Tag's pass put on it, and the default Smart Tag backed
 * by the item's own kind — less anything a person took off by hand.
 *
 * A default is derived here rather than stored anywhere: To-dos and Decisions
 * match exactly what the summary already classifies, so there is nothing
 * for a pass to decide and nothing to write down.
 *
 * An auto-applied id whose tag is no longer a Smart Tag is dropped rather than
 * waited on. Switching a tag off has to take its applications with it now, and
 * the pass that would clear them is up to ninety seconds away.
 *
 * A deleted default stops being derived, which is the only way deleting one
 * can be seen: nothing wrote the derivation down, so unlike a custom
 * Smart Tag there is no stored set for a later pass to replace. An id already
 * stored on an item is left alone — deleting a tag never takes it off content
 * that carries it.
 */
export const effectiveTagIds = (
  item: {
    tagIds?: readonly string[];
    autoTagIds?: readonly string[];
    removedAutoTagIds?: readonly string[];
    kind?: string;
  },
  tags: readonly LiveSummaryTag[]
): string[] => {
  const ids = new Set<string>(item.tagIds ?? []);

  const smart = new Set(tags.filter((t) => t.smart).map((t) => t.id));
  for (const id of item.autoTagIds ?? []) {
    if (smart.has(id)) ids.add(id);
  }
  for (const tag of tags) {
    if (tag.smart && !tag.deleted && tag.defaultKind && tag.defaultKind === item.kind) ids.add(tag.id);
  }

  // A hand removal never reaches a tag the same hand applied, so this runs last
  // without having to order the two against each other.
  for (const id of item.removedAutoTagIds ?? []) {
    if (!(item.tagIds ?? []).includes(id)) ids.delete(id);
  }

  return [...ids];
};

/**
 * The tags an item shows on its rail, in the order it shows them: the tag
 * standing in for the item's own kind first, then the rest in the vocabulary's
 * order.
 *
 * The order is the point: a kind's own tag leads, so the first bead is what
 * says "decision" at a glance, in the same place on every row.
 */
export const orderedTagsFor = (item: LiveSummaryItemData, tags: readonly LiveSummaryTag[]): LiveSummaryTag[] => {
  const ids = new Set(item.tagIds ?? []);
  const on = tags.filter((t) => ids.has(t.id));
  const leads = (t: LiveSummaryTag): number => Number(t.defaultKind === item.kind);
  return on.toSorted((a, b) => leads(b) - leads(a));
};
